/**
 * Secure Token Storage Utility — httpOnly Cookie Migration
 *
 * Auth flow:
 * 1. Backend sets httpOnly cookie on login (already done)
 * 2. Frontend sends `withCredentials: true` on all requests (cookie auto-attached by browser)
 * 3. localStorage token is used ONLY as fallback for API testing tools (curl) that read
 *    the token from the login response — the browser never needs to read it directly
 * 4. On logout, backend deletes the cookie; frontend clears localStorage fallback
 *
 * This design ensures:
 * - XSS cannot steal the httpOnly cookie (browser enforced)
 * - curl/Postman can still test APIs using the Bearer token from login response
 * - Backward-compatible: both cookie and header auth work on the backend
 */

function getStore() {
  try {
    localStorage.setItem('__test__', '1');
    localStorage.removeItem('__test__');
    return localStorage;
  } catch {
    const mem = {};
    return {
      getItem: (k) => mem[k] ?? null,
      setItem: (k, v) => { mem[k] = v; },
      removeItem: (k) => { delete mem[k]; },
    };
  }
}

const store = getStore();

/** Get fallback token for Authorization header (used when cookie is not available) */
export function getToken() {
  return store.getItem('token');
}

/** Store token as fallback (primary auth is the httpOnly cookie set by the server) */
export function setToken(token) {
  store.setItem('token', token);
}

export function getUser() {
  const raw = store.getItem('user');
  if (!raw) return null;
  try { return JSON.parse(raw); } catch { return null; }
}

export function setUser(user) {
  store.setItem('user', JSON.stringify(user));
}

export function clearAuth() {
  ['token', 'user', 'must_reset_password'].forEach(key => store.removeItem(key));
}

export function getMustResetPassword() {
  return store.getItem('must_reset_password') === 'true';
}

export function setMustResetPassword(val) {
  if (val) store.setItem('must_reset_password', 'true');
  else store.removeItem('must_reset_password');
}
